import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { Tabs, useThemeColors } from "../../native-ui";
import TabItem from "../../native-ui/Tabs/TabItem";
import useSafeCallback from "../../hooks/useSafeCallback";
import { connect } from "~/redux/connect";
import { getEditFields, getField, getFieldGridControlSources, getFieldValue, getItemId, getRouter, getSaveResult } from "~/stateGetter";
import identify from "~/identify";
import { useLastTabContext } from "~/native-ui/Tabs/LastTabContext";
import { replace } from 'redux-little-router';
import find from "lodash/find";
import useLatest from "~/hooks/useLatest";

type TabProps = {
    title: string;
    icon?: string;
    iconFamily?: string;
    key: string | number;
    children: ReactNode;
    description?: string;
    disabled?: boolean;
    urlKey?: string;
    variant?: "wizard" | "tabs";
}

type TabsRowProps = {
    tabs: TabProps[];
    defaultValue?: string | number;
    modelId: string;
    view: boolean;

    lastSavedAt?: number | null;
    variant?: TabProps['variant'];
    name?: string
    tabField?: string;
    value?: string | number;
    onChange?: (tab: string | number) => void;
    controlled?: boolean;

    errorTabs?: string[];
}

const TabsRow = React.memo((props: TabsRowProps) => {
    const {lastOpenedTab, setLastOpenedTab } = useLastTabContext();

    const [value, onChange] = React.useState(props.value ?? props.defaultValue ?? lastOpenedTab ?? props.tabs[0].key);

    const resolvedCurrentTab = (props.value ?? value) as string;

    const currentTab = React.useMemo(() => props.tabs.find((tab => tab.key === resolvedCurrentTab)) ?? props.tabs[0], [props.tabs, resolvedCurrentTab]);
    const renderFunc = useSafeCallback(() => {
        return <>{ currentTab?.children }</>
    });

    let firstErrorTab = props.errorTabs[0];
    const latestTab = useLatest(resolvedCurrentTab);


    useEffect(() => {
        if (!firstErrorTab) return;
        if (latestTab.current === firstErrorTab) return;
        if (!props.controlled) onChange(firstErrorTab);
        props.onChange?.(firstErrorTab);
        setLastOpenedTab(firstErrorTab);
    }, [props.controlled, firstErrorTab, props.lastSavedAt]);


    const handleChange = useCallback((nextTab: string) => {
        if (!props.controlled) onChange(nextTab);
        props.onChange?.(nextTab);
        setLastOpenedTab(nextTab);
    }, [props.onChange]);

    const {primaryColor, errorColor } = useThemeColors();

    const activeColor = useMemo(() => props.errorTabs?.includes(resolvedCurrentTab as string) ? errorColor : primaryColor, [props.errorTabs, resolvedCurrentTab]);


    return (
        <Tabs value={currentTab.key as string} onChange={handleChange} activeColor={activeColor} scrollable>
            {
                props.tabs.map((tab, i) => <TabItem
                    key={tab.key ?? i}
                    name={tab.key as string}
                    iconName={tab.icon}
                    iconType={tab.iconFamily}
                    label={tab.title}
                    disabled={tab.disabled}
                    isInvalid={props.errorTabs.includes(tab.key as string)}
                    render={renderFunc}
                    mode={props.variant}
                    index={i}
                />)
            }
        </Tabs>
    )
});


const connectTabsRow = connect(
    (state, {modelId, name, tabs, tabField}: TabsRowProps) => { 
        const editFields = getEditFields(state, modelId);

        const isNewItem = getItemId(state, modelId) === identify.NEW_ITEM;

        const errorGroups = Object.values(editFields)
            .reduce((errorState: Record<string, boolean>, field: { group: string, name: string}) => {
                if (errorState[field.group]) return errorState;
                const hasError = !!getField(state, modelId, identify.EDITFIELD, field.name).validationMessage
                return {
                    ...errorState,
                    [field.group]: hasError,
                }
            }, {} );
        
        const tabfieldValue = getFieldValue(state, modelId, identify.EDITFIELD, tabField)?.value;

        const foundTabKey = (name && findActiveTabKey(tabs, getRouter(state).query[name]))
            ?? (tabField && findActiveTabKey( tabs, tabfieldValue, isNewItem ))
            ?? null


        const gridSources = getFieldGridControlSources(state, modelId);
        return {
            lastSavedAt: getSaveResult(state, modelId)?.finished_at ?? null,
            selectedTab: foundTabKey,
            variant: tabField && tabfieldValue !== "COMPLETED" ? "wizard" : undefined,
            errorTabs: tabs
                .filter((tab) => !!gridSources[tab.key]?.some((fieldGroup) => !!errorGroups[fieldGroup]))
                .map((tab) => tab.key),
        };
    },
    (dispatch, props) => ({
        onSelectTab: (nextTab: string | number) => {
            const urlKey = props.tabs.find((tab) => tab.key === nextTab).urlKey;
            if (!urlKey) return;
            dispatch(replace({ path: "", query: { [props.name]: urlKey }}, { persistQuery: true, forceChangeRootModel: false }))
        },
    }),
    (stateprops, { onSelectTab }, props) => {
        const { errorTabs, lastSavedAt, selectedTab, ...errors } = stateprops;
        return {
            ...props,
            variant: stateprops.variant,
            onChange: props.name ? onSelectTab : props.onChange,
            controlled: props.name ? true : false,
            value: selectedTab ?? props.value,
            lastSavedAt: lastSavedAt,
            errorTabs,
        }
    },
    {
        areStatePropsEqual: (before, after, key) => {
            if (key === 'errorTabs') return new Set([...before.errorTabs, ...after.errorTabs]).size === after.errorTabs.length;
            return Object.is(before, after)
        }
    }
);

function findActiveTabKey(tabs: TabsRowProps['tabs'], foundTabKey?: string | null, isNewItem?: boolean) {
    if (isNewItem) return null;
    if (!foundTabKey) return null;
    if (foundTabKey === "COMPLETED") return null;
    const foundTab = find(tabs, { urlKey: foundTabKey + "" });
    return !foundTab || foundTab?.disabled ? null : foundTab.key;
}


export default connectTabsRow(TabsRow);